import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { FileScan, Ruler, Layers, FileDown, Cpu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ElevationThumb } from "@/components/ElevationThumb";
import { OtpLogin } from "@/components/OtpLogin";

const SAMPLE = {
  tag: "W3", type: "window", width: 2400, height: 1800, columns: [800, 800, 800], rows: [500, 1300],
  cells: [["awning", "fixed", "awning"], ["casement_left", "fixed", "casement_right"]],
  frame_width: 60, mullion_width: 80, sash_width: 55,
};

const FEATURES = [
  { icon: FileScan, t: "Single or compiled PDFs", d: "Upload one drawing or a full schedule. Every W/D tag is detected separately." },
  { icon: Cpu, t: "AI reads the drawing", d: "Dimensions, panel splits and opening types are pulled straight from your PDF." },
  { icon: Ruler, t: "Auto-dimension chains", d: "Panel pitch and overall sizes are drawn and updated as you edit." },
  { icon: FileDown, t: "PDF + DXF output", d: "A3 sheets with title block, and layered DXF files ready for CAD." },
];

export default function Landing() {
  const { user } = useAuth();
  const focusLogin = () => { const el = document.getElementById("otp-email"); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.focus(); };
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100 overflow-hidden">
      <header className="sticky top-0 z-20 h-16 px-6 lg:px-12 flex items-center border-b border-slate-800/80 bg-[#090D16]/90 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <Layers className="text-cyan-400" size={22} />
          <span className="font-heading font-bold text-lg tracking-tight" data-testid="brand-logo">AluDraft<span className="text-cyan-400">.ai</span></span>
        </div>
        <button data-testid="header-login-btn" onClick={focusLogin} className="ml-auto h-9 px-4 rounded-md border border-slate-700 text-sm hover:border-cyan-500 hover:text-cyan-300 transition-colors">Sign in</button>
      </header>

      <section className="relative px-6 lg:px-12 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center cad-grid">
        <div className="rise">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-6">PDF → System aluminium elevation</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Turn any window & door drawing into a clean <span className="text-cyan-400">fabrication-ready</span> elevation.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-400 max-w-xl leading-relaxed">
            Drop an architect's PDF — single sheet or a full schedule. AluDraft extracts every opening and redraws it as a dimensioned system elevation you can edit and export.
          </p>
          <div className="mt-10 max-w-md"><OtpLogin /></div>
        </div>
        <div className="relative rise" style={{ animationDelay: "150ms" }}>
          <div className="relative rounded-lg border border-slate-800 bg-[#0B0F19]/90 p-6 shadow-[0_0_80px_-20px_rgba(6,182,212,0.35)]">
            <div className="flex items-center justify-between mb-4 font-mono text-[11px] uppercase tracking-wider text-slate-500">
              <span>W3 · Elevation</span><span className="text-emerald-400">● Live preview</span>
            </div>
            <div className="aspect-[4/3]" data-testid="landing-preview"><ElevationThumb item={SAMPLE} size={520} dims labels /></div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-20 border-t border-slate-800 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800">
        {FEATURES.map(({ icon: I, t, d }, i) => (
          <div key={t} className="bg-[#090D16] p-8 rise" style={{ animationDelay: `${200 + i * 80}ms` }}>
            <I className="text-cyan-400 mb-5" size={22} />
            <h3 className="font-heading text-lg font-semibold mb-2">{t}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>
      <footer className="px-6 lg:px-12 py-8 font-mono text-xs text-slate-600">AluDraft.ai — elevation drafting for aluminium fabricators</footer>
    </div>
  );
}
